// components/maps/PharmacyMarker.jsx
import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Star, MapPin, Phone, Truck } from 'lucide-react';
import { renderToStaticMarkup } from 'react-dom/server';

const PharmacyMarker = ({ pharmacy, isSelected, onClick }) => {
  // Get coordinates
  const position = pharmacy.location?.coordinates 
    ? [pharmacy.location.coordinates[1], pharmacy.location.coordinates[0]]
    : null;

  if (!position) return null;

  // Create custom icon
  const createCustomIcon = () => {
    const iconColor = isSelected ? '#DC2626' : pharmacy.deliveryService ? '#2563EB' : '#10B981';
    
    const iconHtml = renderToStaticMarkup(
      <div style={{
        position: 'relative',
        width: '32px',
        height: '40px'
      }}>
        <svg 
          width="32" 
          height="40" 
          viewBox="0 0 32 40" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24c0-8.837-7.163-16-16-16z" 
            fill={iconColor}
            stroke="white"
            strokeWidth="2"
          />
          <circle cx="16" cy="16" r="6" fill="white" />
          <text 
            x="16" 
            y="20" 
            textAnchor="middle" 
            fontSize="12" 
            fill={iconColor}
            fontWeight="bold"
          >
            Rx
          </text>
        </svg>
      </div>
    );

    return L.divIcon({
      html: iconHtml,
      className: 'custom-pharmacy-marker',
      iconSize: [32, 40],
      iconAnchor: [16, 40],
      popupAnchor: [0, -40]
    });
  };

  return (
    <Marker
      position={position}
      icon={createCustomIcon()}
      eventHandlers={{
        click: () => onClick(pharmacy)
      }}
    >
      <Popup className="custom-popup" maxWidth={300}>
        <div className="p-2">
          <h3 className="font-semibold text-base text-gray-900 mb-2">
            {pharmacy.name}
          </h3>
          
          {pharmacy.rating?.average > 0 && (
            <div className="flex items-center gap-1 mb-2">
              <Star size={14} className="text-yellow-500 fill-current" />
              <span className="text-sm font-medium">{pharmacy.rating.average.toFixed(1)}</span>
              <span className="text-xs text-gray-500">({pharmacy.rating.count} reviews)</span>
            </div>
          )}
          
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2 text-gray-600">
              <MapPin size={14} className="mt-0.5 flex-shrink-0 text-green-600" />
              <span className="line-clamp-2">{pharmacy.address}</span>
            </div>
            
            {pharmacy.distance && (
              <div className="text-green-600 font-medium">
                📍 {pharmacy.distance.toFixed(1)} km away
              </div>
            )}

            {pharmacy.phone && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone size={14} className="text-green-600" />
                <span>{pharmacy.phone}</span>
              </div>
            )}

            {pharmacy.deliveryService && (
              <div className="flex items-center gap-2 text-blue-600 font-medium">
                <Truck size={14} />
                <span>Delivery Available</span>
              </div>
            )}
          </div>
          
          <div className="mt-3 pt-3 border-t border-gray-200">
            <a
              href={`/pharmacies/${pharmacy._id}`}
              className="text-green-600 hover:text-green-700 font-medium text-sm"
            >
              View Details →
            </a>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

export default PharmacyMarker;